import React from 'react';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { SendConnectionsEmailFormViewmodel } from './send-connections-email-form.viewmodel';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../global/rich-text-editor';
import { FormattedMessage } from 'react-intl';
import { FORM_CONFIG, FieldKey } from './send-connections-email-form.config';
import { TagsInput } from '../../../../../global/tags-input/tags-input';
import { FormFieldInput } from '../../../../../global/form/form-field-input/form-field-input';
import { FormFieldError } from '../../../../../global/form/form-field-input/form-field-error';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { cancelText } from '../../../../../../constants/common-labels.constants';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { FormVmValueType } from '../../../../../../infrastructure/form-viewmodel-controller';

import './send-connections-email-form.scss';
import { SendConnectionsEmailFormHeader } from './send-connections-email-header/send-connections-email-header';
import { LOADING_EMAIL_TEMPLATE_DATA } from '../../../../../_email-templates/email-templates.text';
import { CopyButton } from '../../../../../global/copy-button/copy-button';
import { EMAIL_TEMPLATES_EVENTS } from '../../../../../../tracking-service/tracking-values';

interface IProps {
    vm: SendConnectionsEmailFormViewmodel;
}

const SendConnectionsEmailFormBody = observer(({ vm }: IProps): JSX.Element => {
    const {
        to,
        replyTo,
        cc,
        bcc,
        emailSubject,
        emailBody
    } = vm.getFormFields(FieldKey);

    return (
        <>
            <SendConnectionsEmailFormHeader
                vm={vm}
            />
            <form className='send-connections-email-form'>
                <div className=' oe-form'>
                    <div className=' oe-form__body'>
                        <div className='oe-form__row m-bottom-20px'>
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.TO].labelDescriptor} />}
                                className='w-40'
                            >
                                <TagsInput
                                    tagsList={[]}
                                    setSelectedTags={(email: FormVmValueType) => to.setValue(email)}
                                    selectedTags={vm.getSelectedEmails(to)}
                                    onBlur={to.setTouched}
                                />
                                <FormFieldError field={to} labelDescriptor={FORM_CONFIG[FieldKey.TO].labelDescriptor} />
                            </FormField>
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.REPLY_TO].labelDescriptor} />}
                                className='w-40'
                            >
                                <TagsInput
                                    tagsList={[]}
                                    setSelectedTags={(email: FormVmValueType) => replyTo.setValue(email)}
                                    selectedTags={vm.getSelectedEmails(replyTo)}
                                    onBlur={replyTo.setTouched}
                                />
                                <FormFieldError field={replyTo} labelDescriptor={FORM_CONFIG[FieldKey.REPLY_TO].labelDescriptor} />
                            </FormField>

                        </div>
                        <div className='oe-form__row m-bottom-20px'>
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.CC].labelDescriptor} />}
                                className='w-40'
                            >
                                <TagsInput
                                    tagsList={[]}
                                    setSelectedTags={(email: FormVmValueType) => cc.setValue(email)}
                                    selectedTags={vm.getSelectedEmails(cc)}
                                    onBlur={cc.setTouched}
                                />
                                <FormFieldError field={cc} labelDescriptor={FORM_CONFIG[FieldKey.CC].labelDescriptor} />
                            </FormField>

                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.BCC].labelDescriptor} />}
                                className='w-40'
                            >
                                <TagsInput
                                    tagsList={[]}
                                    setSelectedTags={(email: FormVmValueType) => bcc.setValue(email)}
                                    selectedTags={vm.getSelectedEmails(bcc)}
                                    onBlur={bcc.setTouched}
                                />
                                <FormFieldError field={bcc} labelDescriptor={FORM_CONFIG[FieldKey.BCC].labelDescriptor} />
                            </FormField>
                        </div>
                        <FormFieldInput.Text
                            field={emailSubject}
                            labelDescriptor={FORM_CONFIG[FieldKey.EMAIL_SUBJECT].labelDescriptor}
                            additionalClassName='send-connections-email-form__subject'
                            withError
                            withCopyBtn
                            withoutIcon
                            onCopy={() => {
                                vm.meetingConnectionsFormVm.trackSendConnectionEmailsEvents(
                                    EMAIL_TEMPLATES_EVENTS.COPY_CONNECTIONS_EMAIL_TEXT,
                                    {
                                        ['Field Copied']: 'Subject Line'
                                    }
                                );
                            }}
                        />
                        <FormField
                            className='copy-email-body'
                        >
                            <CopyButton
                                text='Copy Email Body'
                                copyAsHTML
                                copyValue={(emailBody.value as string)}
                                withoutIcon
                                onCopy={() => {
                                    vm.meetingConnectionsFormVm.trackSendConnectionEmailsEvents(
                                        EMAIL_TEMPLATES_EVENTS.COPY_CONNECTIONS_EMAIL_TEXT,
                                        {
                                            ['Field Copied']: 'Email Body'
                                        }
                                    );
                                }}
                            />
                        </FormField>
                        <FormField
                            label={<FormattedMessage {...FORM_CONFIG[FieldKey.EMAIL_BODY].labelDescriptor} />}
                            className='with-editor send-connections-email-form__email-body'
                        >
                            <RichTextEditor
                                className='m-top-0px body-editor'
                                value={emailBody.value as string}
                                onChange={action((event, editor) => {
                                    emailBody.setValue(editor.getData());
                                })}
                                onBlur={emailBody.setTouched}
                            />
                            <FormFieldError field={emailBody} labelDescriptor={FORM_CONFIG[FieldKey.EMAIL_BODY].labelDescriptor} />
                        </FormField>
                    </div>
                </div>
            </form>
        </>
    );
});

const SendConnectionsEmailFormFooter = observer(({ submit, actionButtonText, isSending }): JSX.Element => {
    return (
        <>
            <div className='buttons-block buttons-block--from-right'>
                <DefaultCloseSubWindowButton
                    text={actionButtonText()}
                    modifier={['fat', 'confirm-safe']}
                    onClick={submit}
                    disabled={isSending}
                />
                <DefaultCloseSubWindowButton text={cancelText()} />
            </div>
        </>
    );
});

const SendConnectionsEmailFormPresentation = observer(({ vm }: IProps): JSX.Element => {

    if (vm.isLoading) return (
        <CommonPopupBodyFooterLayout
            className='oe-form'
            body={
                <div>
                    {vm.intl.formatMessage(LOADING_EMAIL_TEMPLATE_DATA)}
                </div>
            }
        />
    );

    return (
        <CommonPopupBodyFooterLayout
            className='hidden-scroll'
            body={<SendConnectionsEmailFormBody vm={vm} />}
            footer={<SendConnectionsEmailFormFooter
                isSending={vm.sending}
                actionButtonText={vm.actionButtonText}
                submit={vm.submitForm}
            />}
        />
    );
});

export { SendConnectionsEmailFormPresentation, SendConnectionsEmailFormBody, SendConnectionsEmailFormFooter };
